import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Upload file
export const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API_BASE}/api/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    withCredentials: true,
  });
  return response.data;
};

// Upload second file for merge
export const uploadSecondFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API_BASE}/api/upload-second`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    withCredentials: true,
  });
  return response.data;
};

// Get preview
export const getPreview = async (page = 1, pageSize = 50) => {
  const response = await api.get(`/api/preview?page=${page}&page_size=${pageSize}`);
  return response.data;
};

// Get info
export const getInfo = async () => {
  const response = await api.get('/api/info');
  return response.data;
};

// Get history
export const getHistory = async () => {
  const response = await api.get('/api/history');
  return response.data;
};

// Undo operation
export const undoOperation = async () => {
  const response = await api.post('/api/undo');
  return response.data;
};

// Filter data
export const filterData = async (conditions) => {
  const response = await api.post('/api/filter', conditions);
  return response.data;
};

// Sort data
export const sortData = async (sortColumns) => {
  const response = await api.post('/api/sort', sortColumns);
  return response.data;
};

// Remove duplicates
export const removeDuplicatesPreview = async (subset, keep = 'first') => {
  const response = await api.post('/api/remove-duplicates/preview', { subset, keep });
  return response.data;
};

export const removeDuplicates = async (subset = null, keep = 'first') => {
  const response = await api.post('/api/remove-duplicates', { subset, keep });
  return response.data;
};

// Rename columns
export const renameColumns = async (renameMap) => {
  const response = await api.post('/api/rename-columns', renameMap);
  return response.data;
};

// Create calculated column
export const createColumn = async (config) => {
  const response = await api.post('/api/create-column', config);
  return response.data;
};

// Aggregate data
export const aggregateData = async (groupBy, aggregations) => {
  const response = await api.post('/api/aggregate', { group_by: groupBy, aggregations });
  return response.data;
};

// Replace values
export const replaceValues = async (config) => {
  const response = await api.post('/api/replace', config);
  return response.data;
};

// List the distinct values of a column, with their counts
export const getColumnValues = async (column, search = '', limit = 200) => {
  const params = new URLSearchParams({ column, limit: String(limit) });
  if (search) params.set('search', search);
  const response = await api.get(`/api/column-values?${params.toString()}`);
  return response.data;
};

// Rewrite several values of one column at once
export const recodeColumn = async (config) => {
  const response = await api.post('/api/recode', config);
  return response.data;
};

// Handle missing values
export const handleMissing = async (config) => {
  const response = await api.post('/api/handle-missing', config);
  return response.data;
};

// Select columns
export const selectColumns = async (columns) => {
  const response = await api.post('/api/select-columns', columns);
  return response.data;
};

// What would join, and under which key — without joining anything.
export const previewMerge = async (config) => {
  const response = await api.post('/api/merge/preview', config);
  return response.data;
};

// Merge data
export const mergeData = async (config) => {
  const response = await api.post('/api/merge', config);
  return response.data;
};

// Stack the second file under the current one
export const appendFiles = async (config) => {
  const response = await api.post('/api/append', config);
  return response.data;
};

// Compare the current file with the second one
export const compareFiles = async (config) => {
  const response = await api.post('/api/compare', config);
  return response.data;
};

// Get fuzzy column matches
export const fuzzyMatchColumns = async (column, targetFile = 'current') => {
  const response = await api.get(`/api/fuzzy-match?column=${encodeURIComponent(column)}&target_file=${targetFile}`);
  return response.data;
};

// Export CSV
export const exportCSV = () => {
  window.open(`${API_BASE}/api/export/csv`, '_blank');
};

// Export Excel
export const exportExcel = () => {
  window.open(`${API_BASE}/api/export/excel`, '_blank');
};

// Clear session
// Le registre d'un projet : ce qui a été fait et ce que ça a donné. Il
// s'écrit tout seul après chaque opération ; ces appels ne font que le lire.
export const getCurrentProject = async () => {
  const response = await api.get('/api/projects/current');
  return response.data;
};

export const listProjects = async () => {
  const response = await api.get('/api/projects');
  return response.data;
};

export const getProject = async (projectId) => {
  const response = await api.get(`/api/projects/${projectId}`);
  return response.data;
};

export const renameProject = async (projectId, name) => {
  const response = await api.post(`/api/projects/${projectId}/rename`, { name });
  return response.data;
};

export const clearSession = async () => {
  const response = await api.post('/api/session/clear');
  return response.data;
};

// Clean emails
// Well-formed addresses that almost certainly reach nobody. Finds only:
// what goes is what the caller ticks and hands back to cleanEmails.
export const findImplausibleEmails = async (config) => {
  const response = await api.post('/api/clean-emails/implausible', config);
  return response.data;
};

export const cleanEmails = async (config) => {
  const response = await api.post('/api/clean-emails', config);
  return response.data;
};

// The malformed addresses behind the count, and the rows each one costs.
// Computed by the same function that removes them, so the list on screen is
// what would go.
export const listInvalidEmails = async (config) => {
  const response = await api.post('/api/clean-emails/invalid', config);
  return response.data;
};

// The whole list, never capped — it exists to be checked in a spreadsheet.
export const exportInvalidEmailsCSV = (column, normalize) => {
  const query = new URLSearchParams({ column, normalize: String(normalize) });
  window.open(`${API_BASE}/api/clean-emails/invalid/export/csv?${query}`, '_blank');
};

export const exportInvalidEmailsExcel = (column, normalize) => {
  const query = new URLSearchParams({ column, normalize: String(normalize) });
  window.open(`${API_BASE}/api/clean-emails/invalid/export/excel?${query}`, '_blank');
};

// Les doublons d'adresse se décident après les autres étapes du nettoyage :
// une ligne déjà retirée comme invalide n'est le doublon de rien. Le panneau
// envoie donc tout le formulaire, pas seulement son propre réglage.
const duplicateQuery = ({ column, normalize, remove_invalid: removeInvalid, remove_values: removeValues }) => {
  const query = new URLSearchParams({
    column,
    normalize: String(normalize),
    remove_invalid: String(removeInvalid),
  });
  (removeValues || []).forEach((value) => query.append('remove_values', value));
  return query;
};

export const listDuplicateEmails = async (config) => {
  const response = await api.post('/api/clean-emails/duplicates', config);
  return response.data;
};

export const exportDuplicateEmailsCSV = (config) => {
  window.open(`${API_BASE}/api/clean-emails/duplicates/export/csv?${duplicateQuery(config)}`, '_blank');
};

export const exportDuplicateEmailsExcel = (config) => {
  window.open(`${API_BASE}/api/clean-emails/duplicates/export/excel?${duplicateQuery(config)}`, '_blank');
};

// ============ NEW API FUNCTIONS ============

// Create pivot table
export const createPivotTable = async (config) => {
  const response = await api.post('/api/pivot-table', config);
  return response.data;
};

// Convert column type
export const convertColumnType = async (config) => {
  const response = await api.post('/api/convert-type', config);
  return response.data;
};

// Split column
export const splitColumn = async (config) => {
  const response = await api.post('/api/split-column', config);
  return response.data;
};

// Concatenate columns
export const concatColumns = async (config) => {
  const response = await api.post('/api/concat-columns', config);
  return response.data;
};

// Get column statistics
export const getColumnStatistics = async (column) => {
  const response = await api.get(`/api/column-statistics?column=${encodeURIComponent(column)}`);
  return response.data;
};

// Get all column statistics
export const getAllStatistics = async () => {
  const response = await api.get('/api/all-statistics');
  return response.data;
};

// Fuzzy search values
export const fuzzySearch = async (config) => {
  const response = await api.post('/api/fuzzy-search', config);
  return response.data;
};

// Fuzzy deduplicate
export const fuzzyDeduplicatePreview = async (config) => {
  const response = await api.post('/api/fuzzy-deduplicate/preview', config);
  return response.data;
};

export const fuzzyDeduplicate = async (config) => {
  const response = await api.post('/api/fuzzy-deduplicate', config);
  return response.data;
};

// ============ SAVED FILES API ============

// Save current file
export const saveFile = async (filename) => {
  const response = await api.post('/api/save-file', { filename });
  return response.data;
};

// Get saved files list
export const getSavedFiles = async () => {
  const response = await api.get('/api/saved-files');
  return response.data;
};

// Delete saved file
export const deleteSavedFile = async (fileId) => {
  const response = await api.delete(`/api/saved-files/${fileId}`);
  return response.data;
};

// Download saved file as CSV
export const downloadSavedFileCsv = (fileId) => {
  return `${API_BASE}/api/saved-files/${fileId}/download/csv`;
};

// Download saved file as Excel
export const downloadSavedFileExcel = (fileId) => {
  return `${API_BASE}/api/saved-files/${fileId}/download/excel`;
};

// Load saved file into current session
export const loadSavedFile = async (fileId) => {
  const response = await api.post(`/api/saved-files/${fileId}/load`);
  return response.data;
};

// ============ RECIPES ============

// What has been done to the current file, as a replayable record
export const getSessionSteps = async () => {
  const response = await api.get('/api/session-steps');
  return response.data;
};

// Keep the current file's steps under a name
export const saveRecipe = async (name) => {
  const response = await api.post('/api/recipes', { name });
  return response.data;
};

// The recipes this browser has kept
export const getRecipes = async () => {
  const response = await api.get('/api/recipes');
  return response.data;
};

// One recipe, with what each step would do to the current file
export const getRecipe = async (recipeId) => {
  const response = await api.get(`/api/recipes/${recipeId}`);
  return response.data;
};

// Replay a recipe — all of it, or its first `upTo` steps
export const applyRecipe = async (recipeId, upTo = null) => {
  const response = await api.post(`/api/recipes/${recipeId}/apply`, { up_to: upTo });
  return response.data;
};

export const renameRecipe = async (recipeId, name) => {
  const response = await api.patch(`/api/recipes/${recipeId}`, { name });
  return response.data;
};

export const deleteRecipe = async (recipeId) => {
  const response = await api.delete(`/api/recipes/${recipeId}`);
  return response.data;
};
